import React, { useEffect, useState } from "react";
import { Plus, Trash, ArrowUp, ArrowDown, Sparkle, SpeakerHigh, FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import { PageLoader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

const VOICES = ["nova", "alloy", "shimmer", "onyx", "echo", "fable", "sage", "ash", "coral"];

export default function AdminTutorial() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [ttsIdx, setTtsIdx] = useState(null);
  const [gen, setGen] = useState(null);
  const [voice, setVoice] = useState("nova");

  useEffect(() => { api.get("/tutorial").then((r) => setDoc(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!doc) return <PageLoader />;

  const setSteps = (steps) => setDoc({ ...doc, steps });
  const setStep = (i, patch) => setSteps(doc.steps.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const addStep = () => setSteps([...(doc.steps || []), { id: `step_${Date.now()}`, title: "New step", body: "", icon: "GraduationCap", image: "", tips: [] }]);
  const delStep = (i) => setSteps(doc.steps.filter((_, k) => k !== i));
  const moveStep = (i, d) => { const a = [...doc.steps]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setSteps(a); };

  const video = doc.video || { chapters: [] };
  const setVideo = (patch) => setDoc({ ...doc, video: { ...video, ...patch } });
  const setChapter = (i, patch) => setVideo({ chapters: (video.chapters || []).map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  const addChapter = () => setVideo({ chapters: [...(video.chapters || []), { title: "New chapter", voiceover: "", audio_url: "" }] });
  const delChapter = (i) => setVideo({ chapters: (video.chapters || []).filter((_, k) => k !== i) });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/tutorial", { title: doc.title, subtitle: doc.subtitle, steps: doc.steps, video: doc.video }); toast.success("Tutorial saved. Organizers see it instantly."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const aiGenerate = async () => {
    setAiBusy(true);
    try { const { data } = await api.post("/admin/tutorial/ai-generate", { scope: "all" }); setGen(data); toast.success("AI draft ready — review and apply below."); }
    catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };
  const applyExplanations = () => {
    if (!gen?.steps) return;
    const byTitle = Object.fromEntries(gen.steps.map((s) => [s.title, s]));
    setSteps(doc.steps.map((s) => (byTitle[s.title] ? { ...s, body: byTitle[s.title].body || s.body, tips: byTitle[s.title].tips || s.tips } : s)));
    toast.success("Applied AI explanations to matching steps.");
  };
  const applyChapters = () => {
    if (!gen?.chapters) return;
    setVideo({ chapters: gen.chapters.map((c) => ({ title: c.title, voiceover: c.voiceover || "", audio_url: "" })) });
    toast.success("Applied AI chapters & voiceover text.");
  };

  const genTts = async (i) => {
    const c = (video.chapters || [])[i];
    if (!c?.voiceover) { toast.error("Add voiceover text first."); return; }
    setTtsIdx(i);
    try {
      const { data } = await api.post("/admin/tutorial/tts", { text: c.voiceover, voice, chapter_index: i });
      setChapter(i, { audio_url: data.audio_url });
      toast.success("Narration audio generated & saved.");
    } catch (e) { toast.error(apiErr(e)); } finally { setTtsIdx(null); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="overline mb-1">Content</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Organizer Tutorial</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={aiGenerate} disabled={aiBusy} className="rounded-full" data-testid="tut-ai-generate">
            {aiBusy ? <CircleNotch size={15} className="animate-spin mr-1" /> : <Sparkle size={15} weight="fill" className="mr-1" />} AI generate
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="tut-save"><FloppyDisk size={15} className="mr-1" /> {saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      {gen && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-6" data-testid="tut-ai-draft">
          <div className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Sparkle size={15} weight="fill" className="text-primary" /> AI draft</div>
          {gen.script && <Textarea readOnly value={gen.script} rows={4} className="text-xs bg-background mb-2" data-testid="tut-ai-script" />}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={applyExplanations} data-testid="tut-apply-expl">Apply explanations to steps</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={applyChapters} data-testid="tut-apply-chapters">Apply chapters & voiceover</Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5 space-y-3 mb-6">
        <div><Label>Title</Label><Input value={doc.title || ""} onChange={(e) => setDoc({ ...doc, title: e.target.value })} className="mt-1.5" data-testid="tut-title" /></div>
        <div><Label>Subtitle</Label><Input value={doc.subtitle || ""} onChange={(e) => setDoc({ ...doc, subtitle: e.target.value })} className="mt-1.5" data-testid="tut-subtitle" /></div>
      </div>

      <Tabs defaultValue="steps">
        <TabsList className="mb-5"><TabsTrigger value="steps" data-testid="tut-tab-steps">Steps ({(doc.steps || []).length})</TabsTrigger><TabsTrigger value="video" data-testid="tut-tab-video">Video & Voiceover</TabsTrigger></TabsList>

        <TabsContent value="steps">
          <div className="space-y-3">
            {(doc.steps || []).map((s, i) => (
              <div key={s.id || i} className="rounded-xl border border-border bg-card p-4" data-testid={`tut-step-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-muted text-xs font-bold">{i + 1}</span>
                  <Input value={s.title} onChange={(e) => setStep(i, { title: e.target.value })} className="h-9 font-medium" data-testid={`tut-step-title-${i}`} />
                  <button onClick={() => moveStep(i, -1)} className="text-muted-foreground p-1" data-testid={`tut-step-up-${i}`}><ArrowUp size={15} /></button>
                  <button onClick={() => moveStep(i, 1)} className="text-muted-foreground p-1" data-testid={`tut-step-down-${i}`}><ArrowDown size={15} /></button>
                  <button onClick={() => delStep(i)} className="text-destructive p-1" data-testid={`tut-step-del-${i}`}><Trash size={15} /></button>
                </div>
                <Textarea value={s.body || ""} onChange={(e) => setStep(i, { body: e.target.value })} rows={2} placeholder="Explanation" className="text-sm mb-2" data-testid={`tut-step-body-${i}`} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={s.image || ""} onChange={(e) => setStep(i, { image: e.target.value })} placeholder="Image URL" className="h-9 text-sm" data-testid={`tut-step-image-${i}`} />
                  <Input value={s.icon || ""} onChange={(e) => setStep(i, { icon: e.target.value })} placeholder="Icon name (e.g. Ticket)" className="h-9 text-sm" data-testid={`tut-step-icon-${i}`} />
                </div>
                <Input value={(s.tips || []).join(" | ")} onChange={(e) => setStep(i, { tips: e.target.value.split("|").map((t) => t.trim()).filter(Boolean) })} placeholder="Tips separated by |" className="h-9 text-sm mt-2" data-testid={`tut-step-tips-${i}`} />
              </div>
            ))}
            <Button variant="outline" onClick={addStep} className="rounded-full" data-testid="tut-add-step"><Plus size={15} className="mr-1" /> Add step</Button>
          </div>
        </TabsContent>

        <TabsContent value="video">
          <div className="rounded-xl border border-border bg-card p-5 space-y-3 mb-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Video URL (YouTube, Vimeo or direct MP4)</Label><Input value={video.url || ""} onChange={(e) => setVideo({ url: e.target.value })} className="mt-1.5" placeholder="https://youtube.com/watch?v=… or https://…/video.mp4" data-testid="tut-video-url" /></div>
              <div><Label>Poster image URL</Label><Input value={video.poster || ""} onChange={(e) => setVideo({ poster: e.target.value })} className="mt-1.5" data-testid="tut-video-poster" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="mb-0">Narration voice</Label>
              <Select value={voice} onValueChange={setVoice}><SelectTrigger className="w-32 h-9" data-testid="tut-voice"><SelectValue /></SelectTrigger><SelectContent>{VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
              <span className="text-xs text-muted-foreground">Used when generating chapter narration.</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="overline">Chapters & voiceover</div>
            {(video.chapters || []).map((c, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4" data-testid={`tut-chapter-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-muted text-xs font-bold">{i + 1}</span>
                  <Input value={c.title} onChange={(e) => setChapter(i, { title: e.target.value })} className="h-9 font-medium" data-testid={`tut-chapter-title-${i}`} />
                  <button onClick={() => delChapter(i)} className="text-destructive p-1" data-testid={`tut-chapter-del-${i}`}><Trash size={15} /></button>
                </div>
                <Textarea value={c.voiceover || ""} onChange={(e) => setChapter(i, { voiceover: e.target.value })} rows={2} placeholder="Voiceover script for this chapter" className="text-sm" data-testid={`tut-chapter-vo-${i}`} />
                <div className="flex items-center gap-3 mt-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => genTts(i)} disabled={ttsIdx === i} data-testid={`tut-tts-${i}`}>
                    {ttsIdx === i ? <CircleNotch size={14} className="animate-spin mr-1" /> : <SpeakerHigh size={14} className="mr-1" />} Generate narration
                  </Button>
                  {c.audio_url && <audio controls src={c.audio_url} className="h-8" data-testid={`tut-chapter-audio-${i}`} />}
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addChapter} className="rounded-full" data-testid="tut-add-chapter"><Plus size={15} className="mr-1" /> Add chapter</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
